import { Check } from 'lucide-react'

import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard'

/**
 * A labelled value in a sheet header. The whole chip copies the value: these
 * are transcribed into other systems, not read, so the copy is the chip's only
 * job. A check replaces the label while the value is fresh on the clipboard.
 */
export function CopyChip({
  label,
  value,
  copyValue = value,
}: {
  label: string
  value: string
  /** What lands on the clipboard when `value` is a shortened display form. */
  copyValue?: string
}) {
  const { copy, isCopied } = useCopyToClipboard()

  return (
    <button
      type="button"
      title={`Copy ${label} ${copyValue}`}
      aria-label={`Copy ${label} ${value}`}
      onClick={() => void copy(copyValue, { timeout: 1500, withToast: false })}
      className="bg-app border-neutral-subtle-border hover:border-primary-subtle-border hover:bg-primary-subtle-bg focus-visible:ring-primary-solid inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 transition-colors focus-visible:ring-2 focus-visible:outline-none">
      {isCopied ? (
        <Check className="text-success-solid size-3" aria-hidden />
      ) : (
        <span className="text-muted-foreground text-xs font-medium">
          {label}
        </span>
      )}
      <span className="text-text-heading max-w-[10rem] truncate font-mono text-xs">
        {value}
      </span>
      <span className="sr-only" aria-live="polite">
        {isCopied ? 'Copied' : ''}
      </span>
    </button>
  )
}
